<!--
  BooleanField — boolean renderer; the chrome-free inner GridCell hosts. The
  checkbox is the whole control (no separate edit surface), so clicking toggles
  and commits immediately; read-only fields show a ✓/✗ icon when set. Keyboard
  activation (GridCell sets `mode: "editing"`) flips the value on its edge.
-->
<template>
  <div class="boolean-field">
    <input v-if="editable" type="checkbox" :checked="!!modelValue" @change="onChange" />
    <span v-else-if="isSet" class="icon" :class="sizeClass">
      <font-awesome-icon
        :icon="modelValue ? 'check' : 'times'"
        :class="modelValue ? 'is-success' : 'is-danger'"
      />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue"
import type { Field } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const editable = computed(() => !!props.field.isEditable)

const isSet = computed(() => props.modelValue != null && props.modelValue !== "")
const sizeClass = computed(() => textSizeClass(props.size))

// Toggling is atomic — emit the new value and commit it straight away
// (interaction persists; no advance).
function toggle(value: boolean) {
  emit("update:modelValue", value)
  emit("commit")
}

function onChange(event: Event) {
  toggle((event.target as HTMLInputElement).checked)
}

// Keyboard activation: GridCell sets `mode: "editing"` (Space/Enter) — flip on its edge.
watch(
  () => props.mode === "editing",
  (on) => on && editable.value && toggle(!props.modelValue)
)
</script>

<style>
.boolean-field {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  input[type="checkbox"] {
    cursor: pointer;
  }
}

.grid-editor .boolean-field input[type="checkbox"] {
  margin-top: -2px;
}
</style>
